// apps/admin/src/pages/admin/StoreSeoTab.js
// Store Admin SEO Command Center: Manage on-page SEO, Google Search SERP simulator,
// keywords, XML sitemaps, and external local citations & backlinks for individual retail stores.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getSites, getSite, updateSite } from "../../services/sites";
import Loader, { Spinner } from "../../components/Loader";
import "./StoreSeoTab.css";

const STORE_EXTERNAL_CITATIONS = [
  {
    id: "gbp",
    name: "Google Business Profile",
    url: "https://business.google.com",
    category: "Local Maps & Discovery",
    da: 92,
    priority: "critical",
    actionUrl: "https://business.google.com/create",
    instructions: "Add your store name, physical shop address or delivery area, and paste your live website URL in the 'Website' field. This enables Google Maps 3-Pack rankings.",
  },
  {
    id: "justdial",
    name: "Justdial Local Directory",
    url: "https://www.justdial.com",
    category: "Indian Local Directories",
    da: 84,
    priority: "critical",
    actionUrl: "https://www.justdial.com/Free-Listing",
    instructions: "Register your free store listing on Justdial. Add your phone number and online store website URL so local buyers can order directly.",
  },
  {
    id: "indiamart",
    name: "IndiaMART Seller Catalog",
    url: "https://seller.indiamart.com",
    category: "B2B & Retail Marketplace",
    da: 84,
    priority: "critical",
    actionUrl: "https://seller.indiamart.com",
    instructions: "Create a verified seller profile on IndiaMART. Add your store catalog link so wholesale and retail buyers can view all products on your website.",
  },
  {
    id: "instagram",
    name: "Instagram Business Bio & Action Link",
    url: "https://www.instagram.com",
    category: "Social Commerce",
    da: 93,
    priority: "critical",
    actionUrl: "https://www.instagram.com",
    instructions: "Switch to a free Instagram Creator/Business account. Paste your online store URL directly into your profile bio and add 'Order Online' call to action.",
  },
  {
    id: "whatsapp",
    name: "WhatsApp Business Catalog & Profile",
    url: "https://web.whatsapp.com",
    category: "Social Commerce",
    da: 90,
    priority: "critical",
    actionUrl: "https://www.whatsapp.com/business",
    instructions: "In WhatsApp Business App, go to Business Profile -> Website. Paste your store link so every customer chat can browse your catalog with 1 click.",
  },
  {
    id: "facebook",
    name: "Facebook Page 'Shop on Website' CTA",
    url: "https://www.facebook.com",
    category: "Social Commerce",
    da: 96,
    priority: "recommended",
    actionUrl: "https://www.facebook.com/pages/create",
    instructions: "Create or edit your store's Facebook Page. Click 'Add Action Button', select 'Shop on Website', and paste your store URL.",
  },
  {
    id: "sulekha",
    name: "Sulekha City Business Directory",
    url: "https://www.sulekha.com",
    category: "Indian Local Directories",
    da: 74,
    priority: "recommended",
    actionUrl: "https://www.sulekha.com",
    instructions: "Submit your store to Sulekha under your city and product category (clothing, food, retail, gifts).",
  },
  {
    id: "tradeindia",
    name: "TradeIndia Seller Profile",
    url: "https://www.tradeindia.com",
    category: "B2B & Retail Marketplace",
    da: 79,
    priority: "recommended",
    actionUrl: "https://www.tradeindia.com",
    instructions: "List your retail/wholesale store on TradeIndia with your official website URL for verified buyer inquiries.",
  },
  {
    id: "pinterest",
    name: "Pinterest Product Catalog & Pins",
    url: "https://www.pinterest.com",
    category: "Visual Search & Discovery",
    da: 94,
    priority: "recommended",
    actionUrl: "https://business.pinterest.com",
    instructions: "Create a free Pinterest Business account. Claim your website domain and create product boards linking directly to your store items.",
  },
  {
    id: "bingplaces",
    name: "Bing Places for Business",
    url: "https://www.bingplaces.com",
    category: "Local Maps & Discovery",
    da: 94,
    priority: "recommended",
    actionUrl: "https://www.bingplaces.com",
    instructions: "Import your Google Business Profile with 1 click to rank on Bing, Yahoo, and Windows desktop search.",
  },
  {
    id: "indianyellowpages",
    name: "Indian Yellow Pages",
    url: "https://www.indianyellowpages.com",
    category: "Indian Local Directories",
    da: 68,
    priority: "optional",
    actionUrl: "https://www.indianyellowpages.com",
    instructions: "Submit free listing with your city, business category, and store URL.",
  },
  {
    id: "webindia123",
    name: "WebIndia123 Local SME Hub",
    url: "https://www.webindia123.com",
    category: "Indian Local Directories",
    da: 77,
    priority: "optional",
    actionUrl: "https://www.webindia123.com/yellowpages",
    instructions: "Register your business in WebIndia123 yellow pages.",
  },
  {
    id: "magicpin",
    name: "Magicpin Hyperlocal Merchant Hub",
    url: "https://magicpin.in",
    category: "Hyperlocal Discovery",
    da: 65,
    priority: "optional",
    actionUrl: "https://magicpin.in/partner",
    instructions: "Claim your store listing on Magicpin so nearby shoppers searching for retail stores in your pincode can discover your website.",
  }
];

export default function StoreSeoTab({ user, notify }) {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [currentSite, setCurrentSite] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingSite, setLoadingSite] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sub-Navigation: internal | external
  const [activeTab, setActiveTab] = useState("internal");

  // SEO Form state
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");

  // External SEO state (stored per site)
  const [externalBacklinks, setExternalBacklinks] = useState({});
  const [storeKitModal, setStoreKitModal] = useState(null);
  const [storeMarkDoneModal, setStoreMarkDoneModal] = useState(null);
  const [savingCitation, setSavingCitation] = useState(false);

  // Load sites for this store owner
  useEffect(() => {
    let active = true;
    getSites()
      .then((res) => {
        if (!active) return;
        const list = res?.data?.sites || [];
        setSites(list);
        if (list.length > 0) {
          setSelectedSiteId(list[0].id || list[0].slug);
        }
      })
      .catch((err) => {
        if (active) notify(err?.response?.data?.message || "Failed to load stores", "error");
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => {
      active = false;
    };
  }, [notify]);

  // Load selected site details & SEO
  const loadSiteData = useCallback(async (id) => {
    if (!id) return;
    setLoadingSite(true);
    try {
      const res = await getSite(id);
      const site = res?.data?.site;
      if (site) {
        setCurrentSite(site);
        setSeoTitle(site.seo?.title || "");
        setSeoDescription(site.seo?.description || "");
        setKeywords(Array.isArray(site.seo?.keywords) ? site.seo.keywords : []);
        setExternalBacklinks(site.seo?.externalBacklinks || {});
      }
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to load website details", "error");
    } finally {
      setLoadingSite(false);
    }
  }, [notify]);

  useEffect(() => {
    if (selectedSiteId) {
      loadSiteData(selectedSiteId);
    }
  }, [selectedSiteId, loadSiteData]);

  // Compute Store SEO Health Score (0 - 100)
  const healthScore = useMemo(() => {
    let score = 20; // base score for having store live with SSL
    const titleVal = (seoTitle || currentSite?.seoDefaults?.title || "").trim();
    const descVal = (seoDescription || currentSite?.seoDefaults?.description || "").trim();

    if (titleVal.length > 0) score += 20;
    if (titleVal.length > 10 && titleVal.length <= 60) score += 15;
    if (descVal.length > 0) score += 20;
    if (descVal.length >= 50 && descVal.length <= 160) score += 15;
    if (keywords.length >= 3) score += 10;

    return Math.min(100, score);
  }, [seoTitle, seoDescription, keywords, currentSite]);

  // Completed external citations count & Local Visibility score
  const completedExternalCount = useMemo(() => {
    return Object.values(externalBacklinks).filter((x) => x?.status === "done").length;
  }, [externalBacklinks]);

  const localVisibilityScore = useMemo(() => {
    const total = STORE_EXTERNAL_CITATIONS.length;
    return Math.min(100, Math.round((completedExternalCount / total) * 100));
  }, [completedExternalCount]);

  // Handlers for keywords
  const handleAddKeyword = (e) => {
    e.preventDefault();
    const kw = newKeyword.trim().toLowerCase();
    if (kw && !keywords.includes(kw) && keywords.length < 20) {
      setKeywords([...keywords, kw]);
      setNewKeyword("");
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setKeywords(keywords.filter((_, idx) => idx !== indexToRemove));
  };

  const handleAddSuggestedKeyword = (kw) => {
    if (!keywords.includes(kw) && keywords.length < 20) {
      setKeywords([...keywords, kw]);
    }
  };

  // Helper buttons to populate defaults
  const handleUseDefaultTitle = () => {
    if (currentSite?.seoDefaults?.title) {
      setSeoTitle(currentSite.seoDefaults.title);
    }
  };

  const handleUseDefaultDesc = () => {
    if (currentSite?.seoDefaults?.description) {
      setSeoDescription(currentSite.seoDefaults.description);
    }
  };

  // Save changes
  const handleSaveSeo = async () => {
    if (!selectedSiteId) return;
    setSaving(true);
    try {
      await updateSite(selectedSiteId, {
        seo: {
          title: seoTitle.trim(),
          description: seoDescription.trim(),
          keywords: keywords,
          externalBacklinks: externalBacklinks,
        },
      });
      notify("Store SEO settings saved successfully!", "success");
      await loadSiteData(selectedSiteId);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to save SEO settings", "error");
    } finally {
      setSaving(false);
    }
  };

  // Copy sitemap URL
  const copySitemap = () => {
    const sitemapUrl = `https://${currentSite?.domain || currentSite?.slug + ".ownezzy.com"}/sitemap.xml`;
    navigator.clipboard?.writeText(sitemapUrl);
    notify("Sitemap URL copied to clipboard!", "success");
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text);
    notify(`${label} copied to clipboard!`, "success");
  };

  // Open modal to mark citation as Done
  const handleOpenStoreMarkDone = (item) => {
    const existing = externalBacklinks[item.id] || {};
    setStoreMarkDoneModal({
      citation: item,
      publishedUrl: existing.publishedUrl || "",
      notes: existing.notes || "",
    });
  };

  // Confirm mark citation done with live URL
  const handleConfirmStoreMarkDone = async (e) => {
    e?.preventDefault();
    if (!storeMarkDoneModal?.citation || !selectedSiteId) return;
    const url = (storeMarkDoneModal.publishedUrl || "").trim();
    if (!url) {
      notify("Please provide the published URL where your store profile is live", "error");
      return;
    }
    setSavingCitation(true);
    const updated = {
      ...externalBacklinks,
      [storeMarkDoneModal.citation.id]: {
        status: "done",
        publishedUrl: url,
        notes: storeMarkDoneModal.notes || "",
        completedAt: new Date().toISOString(),
      },
    };
    try {
      await updateSite(selectedSiteId, {
        seo: {
          title: seoTitle.trim(),
          description: seoDescription.trim(),
          keywords: keywords,
          externalBacklinks: updated,
        },
      });
      setExternalBacklinks(updated);
      setStoreMarkDoneModal(null);
      notify(`✓ Verified and marked ${storeMarkDoneModal.citation.name} as Done!`, "success");
    } catch (err) {
      notify("Failed to save citation status", "error");
    } finally {
      setSavingCitation(false);
    }
  };

  // Mark citation as pending
  const handleStoreMarkPending = async (item) => {
    if (!selectedSiteId) return;
    const updated = { ...externalBacklinks };
    delete updated[item.id];
    try {
      await updateSite(selectedSiteId, {
        seo: {
          title: seoTitle.trim(),
          description: seoDescription.trim(),
          keywords: keywords,
          externalBacklinks: updated,
        },
      });
      setExternalBacklinks(updated);
      notify(`Re-opened ${item.name} as Pending`, "success");
    } catch (err) {
      notify("Failed to update status", "error");
    }
  };

  if (loadingList) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center" }}>
        <Loader text="Loading your store SEO..." />
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="store-seo-container">
        <div className="page-heading">
          <div>
            <p className="eyebrow">STORE SEO</p>
            <h1>Search Engine Optimization (SEO)</h1>
            <p className="subheading">You have not created any stores yet. Create a website first to manage its SEO.</p>
          </div>
        </div>
      </div>
    );
  }

  const liveStoreUrl = currentSite ? `https://${currentSite.domain || currentSite.slug + ".ownezzy.com"}` : "";
  const displayTitle = seoTitle || currentSite?.seoDefaults?.title || `${currentSite?.name || "Your Store"} – Buy Online`;
  const displayDesc = seoDescription || currentSite?.seoDefaults?.description || `Buy products online at ${currentSite?.name || "Your Store"} with fast delivery and UPI.`;
  const suggestedKws = currentSite?.seoDefaults?.keywords || ["buy online", "whatsapp store", "direct upi", "cod"];

  return (
    <div className="store-seo-container">
      {/* Page Heading & Multi-Store Picker */}
      <div className="store-seo-header">
        <div>
          <p className="eyebrow" style={{ fontSize: "11px", color: "#64748b", margin: "0 0 4px", fontWeight: 700 }}>
            STORE SEO COMMAND CENTER
          </p>
          <h1 style={{ fontSize: "22px", margin: "0 0 6px", fontWeight: 800, color: "#0f172a" }}>
            Search Engine Optimization (SEO)
          </h1>
          <p style={{ margin: 0, fontSize: "13.5px", color: "#64748b" }}>
            Configure on-page metadata, Google Search snippets, and external local citations for {currentSite?.name || "your store"}.
          </p>
        </div>

        {sites.length > 1 && (
          <div className="store-picker-box">
            <span style={{ fontSize: "13px", color: "#64748b" }}>Website:</span>
            <select
              className="store-picker-select"
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
            >
              {sites.map((s) => (
                <option key={s.id || s.slug} value={s.id || s.slug}>
                  {s.name} ({s.domain || s.slug})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Sub-Navigation Switcher: Internal vs External */}
      <div className="store-seo-subnav">
        <button
          type="button"
          className={`store-seo-tab-btn ${activeTab === "internal" ? "active" : ""}`}
          onClick={() => setActiveTab("internal")}
        >
          <span>🏠 On-Page &amp; SERP (Internal SEO)</span>
        </button>
        <button
          type="button"
          className={`store-seo-tab-btn ${activeTab === "external" ? "active" : ""}`}
          onClick={() => setActiveTab("external")}
        >
          <span>🌐 Backlinks &amp; Citations (External SEO)</span>
          <span className="store-tab-counter">
            {completedExternalCount} / {STORE_EXTERNAL_CITATIONS.length} Done
          </span>
        </button>
      </div>

      {loadingSite ? (
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <Loader text="Fetching store details..." />
        </div>
      ) : (
        <>
          {/* TAB 1: INTERNAL SEO */}
          {activeTab === "internal" && (
            <>
              {/* SEO Health Score Banner */}
              <div
                className="store-seo-score-banner"
                style={{ "--score-deg": `${(healthScore / 100) * 360}deg` }}
              >
                <div className="store-score-left">
                  <div className="store-score-gauge">
                    <div className="store-score-gauge-inner">
                      <span className="store-score-num">{healthScore}%</span>
                      <span className="store-score-sub">SEO</span>
                    </div>
                  </div>
                  <div className="store-score-info">
                    <h2>{currentSite?.name || "Your Store"} SEO Health: {healthScore >= 80 ? "Excellent" : "Good"}</h2>
                    <p>
                      Optimized for Google Crawlers, Mobile Search &amp; WhatsApp Share Previews.
                    </p>
                  </div>
                </div>

                <div className="store-score-checklist">
                  <span className={`store-score-pill ${seoTitle.length > 0 && seoTitle.length <= 60 ? "passed" : "pending"}`}>
                    {seoTitle.length > 0 && seoTitle.length <= 60 ? "✓ Title Optimal" : "⚠️ Check Title Length"}
                  </span>
                  <span className={`store-score-pill ${seoDescription.length >= 50 && seoDescription.length <= 160 ? "passed" : "pending"}`}>
                    {seoDescription.length >= 50 && seoDescription.length <= 160 ? "✓ Meta Optimal" : "⚠️ Check Meta Length"}
                  </span>
                  <span className={`store-score-pill ${keywords.length >= 3 ? "passed" : "pending"}`}>
                    {keywords.length >= 3 ? "✓ Keywords Added" : "⚠️ Add 3+ Keywords"}
                  </span>
                  <span className="store-score-pill passed">
                    ✓ SSL &amp; Sitemap Active
                  </span>
                </div>
              </div>

              {/* Google Search Result (SERP) Simulator */}
              <div className="store-seo-card">
                <div className="store-seo-card-header">
                  <h3>
                    <span>🔍</span> Google Search Result Preview (SERP)
                  </h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Live simulator of your store snippet on Google
                  </span>
                </div>

                <div className="store-serp-preview-box">
                  <div className="store-serp-url-row">
                    <div className="store-serp-favicon">
                      {currentSite?.name ? currentSite.name.charAt(0).toUpperCase() : "O"}
                    </div>
                    <span className="store-serp-url">{liveStoreUrl}</span>
                  </div>
                  <div className="store-serp-title">{displayTitle}</div>
                  <p className="store-serp-desc">{displayDesc}</p>
                </div>
              </div>

              {/* Store SEO Settings Form */}
              <div className="store-seo-card">
                <div className="store-seo-card-header">
                  <h3>
                    <span>⚙️</span> Edit Store Search Metadata
                  </h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Changes update your live HTML title &amp; meta description tags
                  </span>
                </div>

                {/* Store SEO Title */}
                <div className="store-seo-field">
                  <label>
                    <span>Store Page Title (Shown on Google &amp; Browser Tabs)</span>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <button
                        type="button"
                        className="store-field-helper-btn"
                        onClick={handleUseDefaultTitle}
                        title="Fill with recommended default title for this store type"
                      >
                        Use Recommended Title
                      </button>
                      <span className={`store-char-badge ${seoTitle.length <= 60 ? "optimal" : "warning"}`}>
                        {seoTitle.length} / 60 chars {seoTitle.length > 60 ? "(Too long)" : ""}
                      </span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder={currentSite?.seoDefaults?.title || "e.g. Trendy Boutique – Buy Saree & Kurti Online"}
                    maxLength={70}
                  />
                  <small>Appears as the blue clickable headline in Google Search results. Keep under 60 characters.</small>
                </div>

                {/* Store SEO Description */}
                <div className="store-seo-field">
                  <label>
                    <span>Meta Description (Snippet in Google Search)</span>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <button
                        type="button"
                        className="store-field-helper-btn"
                        onClick={handleUseDefaultDesc}
                        title="Fill with recommended default description"
                      >
                        Use Recommended Description
                      </button>
                      <span className={`store-char-badge ${seoDescription.length >= 50 && seoDescription.length <= 160 ? "optimal" : "warning"}`}>
                        {seoDescription.length} / 160 chars
                      </span>
                    </div>
                  </label>
                  <textarea
                    rows={3}
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    placeholder={currentSite?.seoDefaults?.description || "e.g. Shop latest collections online. Enjoy 100% genuine products, fast delivery across India, and secure UPI payment."}
                    maxLength={170}
                  />
                  <small>Encourages shoppers to click your link on Google. Mention your main products, free shipping, or direct UPI.</small>
                </div>

                {/* Store Search Keywords */}
                <div className="store-seo-field">
                  <label>
                    <span>Target Search Keywords ({keywords.length}/20)</span>
                  </label>
                  <form onSubmit={handleAddKeyword} style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                    <input
                      type="text"
                      placeholder="Type a keyword and press Enter (e.g. kurti, cotton saree, shoes)"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="store-btn-add-kw">
                      + Add
                    </button>
                  </form>

                  {/* Active Keywords list */}
                  {keywords.length > 0 && (
                    <div className="store-kw-pill-wrap">
                      {keywords.map((kw, idx) => (
                        <span key={idx} className="store-kw-pill">
                          {kw}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(idx)}
                            aria-label={`Remove ${kw}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Suggestions */}
                  <div className="suggested-kw-strip">
                    <span>Suggested for your store:</span>
                    {suggestedKws.slice(0, 6).map((kw) => (
                      <button
                        key={kw}
                        type="button"
                        className="suggested-kw-btn"
                        onClick={() => handleAddSuggestedKeyword(kw)}
                      >
                        + {kw}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Bar */}
                <div className="store-seo-actions">
                  <button
                    type="button"
                    className="btn-store-save-seo"
                    onClick={handleSaveSeo}
                    disabled={saving}
                  >
                    {saving ? <Spinner size={16} /> : "💾"}
                    <span>{saving ? "Saving Changes..." : "Save SEO Settings"}</span>
                  </button>
                </div>
              </div>

              {/* XML Sitemap & Google Search Console Card */}
              <div className="store-sitemap-card">
                <div className="store-sitemap-info">
                  <h4>🗺️ Automatic XML Sitemap</h4>
                  <p>{liveStoreUrl}/sitemap.xml</p>
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                    Generated automatically with all products, collections, and policy pages.
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button
                    type="button"
                    className="store-field-helper-btn"
                    style={{ padding: "8px 14px" }}
                    onClick={copySitemap}
                  >
                    📋 Copy Sitemap URL
                  </button>
                  <a
                    href={`${liveStoreUrl}/sitemap.xml`}
                    target="_blank"
                    rel="noreferrer"
                    className="store-field-helper-btn"
                    style={{ padding: "8px 14px", textDecoration: "none" }}
                  >
                    Open Sitemap ↗
                  </a>
                </div>
              </div>
            </>
          )}

          {/* TAB 2: EXTERNAL SEO (BACKLINKS & LOCAL CITATIONS) */}
          {activeTab === "external" && (
            <>
              {/* Local Visibility Score & External Citations Banner */}
              <div className="store-local-seo-banner">
                <div className="store-local-seo-top">
                  <div>
                    <span className="store-local-badge">LOCAL SEARCH VISIBILITY</span>
                    <h3 style={{ margin: "6px 0 4px", fontSize: "18px", color: "#0f172a" }}>
                      Local Citations &amp; Backlinks Checklist
                    </h3>
                    <p style={{ margin: 0, fontSize: "13px", color: "#475569" }}>
                      Listing your store on these high-authority local portals helps shoppers in your city find your website on Google Maps and organic search.
                    </p>
                  </div>
                  <div className="store-local-meter-box">
                    <span className="store-local-meter-num">{localVisibilityScore}%</span>
                    <span className="store-local-meter-label">Local Visibility</span>
                  </div>
                </div>

                <div className="store-progress-track">
                  <div
                    className="store-progress-fill"
                    style={{ width: `${Math.max(5, localVisibilityScore)}%` }}
                  ></div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#64748b", marginTop: "6px" }}>
                  <span>{completedExternalCount} of {STORE_EXTERNAL_CITATIONS.length} citations verified</span>
                  <span>{completedExternalCount >= 8 ? "🚀 High Local Authority" : "⚡ Complete 5+ to boost local Google ranking"}</span>
                </div>
              </div>

              {/* Citations Table */}
              <div className="store-seo-card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="store-seo-card-header" style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "15px" }}>Verified Local &amp; High-DA Backlink Targets</h3>
                    <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                      Submit your store URL to each platform. Use the pre-filled Content Kit for 1-click copy-pasting.
                    </p>
                  </div>
                </div>

                <div className="store-citations-table-wrap">
                  <table className="store-citations-table">
                    <thead>
                      <tr>
                        <th style={{ width: "120px" }}>Status</th>
                        <th>Target Platform</th>
                        <th style={{ width: "140px" }}>Category</th>
                        <th style={{ width: "80px" }}>DA Score</th>
                        <th style={{ width: "100px" }}>Priority</th>
                        <th style={{ width: "220px", textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {STORE_EXTERNAL_CITATIONS.map((cit) => {
                        const saved = externalBacklinks[cit.id];
                        const isDone = saved?.status === "done";
                        return (
                          <tr key={cit.id} className={isDone ? "is-done" : ""}>
                            <td>
                              {isDone ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
                                  <button
                                    type="button"
                                    className="store-btn-done done"
                                    onClick={() => handleStoreMarkPending(cit)}
                                    title="Click to mark as Pending"
                                  >
                                    ✓ Done
                                  </button>
                                  {saved.publishedUrl && (
                                    <a
                                      href={saved.publishedUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="store-live-link"
                                      title={saved.publishedUrl}
                                    >
                                      🔗 Live Link ↗
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="store-btn-done"
                                  onClick={() => handleOpenStoreMarkDone(cit)}
                                  title="Click to paste published profile URL and mark Done"
                                >
                                  Mark Done
                                </button>
                              )}
                            </td>

                            <td>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>{cit.name}</strong>
                                <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                                  {cit.instructions}
                                </span>
                              </div>
                            </td>

                            <td>
                              <span className="store-cat-pill">{cit.category}</span>
                            </td>

                            <td>
                              <span className="store-da-pill">DA {cit.da}</span>
                            </td>

                            <td>
                              <span className={`store-priority-pill ${cit.priority}`}>{cit.priority}</span>
                            </td>

                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              {isDone && (
                                <button
                                  type="button"
                                  className="store-btn-action"
                                  onClick={() => handleOpenStoreMarkDone(cit)}
                                  style={{ marginRight: "6px" }}
                                  title="Edit live profile URL"
                                >
                                  ✏️ Edit
                                </button>
                              )}
                              <button
                                type="button"
                                className="store-btn-action"
                                onClick={() => setStoreKitModal(cit)}
                                style={{ marginRight: "6px" }}
                                title="View ready-to-copy store details"
                              >
                                📋 Content Kit
                              </button>
                              <a
                                href={cit.actionUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="store-btn-action submit-btn"
                              >
                                Open ↗
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* STORE CONTENT KIT MODAL */}
      {storeKitModal && (
        <div className="store-modal-overlay" onClick={() => setStoreKitModal(null)}>
          <div className="store-modal-card" onClick={(e) => e.stopPropagation()}>
            <header className="store-modal-header">
              <div>
                <h3 style={{ margin: "0 0 2px" }}>📋 Store Submission Kit: {storeKitModal.name}</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#94a3b8" }}>
                  Copy-paste these verified business details when creating your listing on {storeKitModal.name}.
                </p>
              </div>
              <button
                type="button"
                className="store-modal-close"
                onClick={() => setStoreKitModal(null)}
              >
                ✕
              </button>
            </header>

            <div className="store-modal-body">
              <div className="store-kit-field">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label>Business / Store Title</label>
                  <button
                    type="button"
                    className="store-copy-btn"
                    onClick={() => copyToClipboard(currentSite?.name || "Your Store", "Store Title")}
                  >
                    Copy
                  </button>
                </div>
                <input type="text" readOnly value={currentSite?.name || ""} />
              </div>

              <div className="store-kit-field">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label>Store Website URL (Target Backlink)</label>
                  <button
                    type="button"
                    className="store-copy-btn"
                    onClick={() => copyToClipboard(liveStoreUrl, "Website URL")}
                  >
                    Copy URL
                  </button>
                </div>
                <input type="text" readOnly value={liveStoreUrl} />
              </div>

              <div className="store-kit-field">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label>Tagline / Headline</label>
                  <button
                    type="button"
                    className="store-copy-btn"
                    onClick={() => copyToClipboard(displayTitle, "Tagline")}
                  >
                    Copy
                  </button>
                </div>
                <input type="text" readOnly value={displayTitle} />
              </div>

              <div className="store-kit-field">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label>Business Description</label>
                  <button
                    type="button"
                    className="store-copy-btn"
                    onClick={() => copyToClipboard(displayDesc, "Description")}
                  >
                    Copy
                  </button>
                </div>
                <textarea rows={4} readOnly value={displayDesc} />
              </div>

              <div className="store-kit-field">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label>Keywords / Categories</label>
                  <button
                    type="button"
                    className="store-copy-btn"
                    onClick={() => copyToClipboard(keywords.join(", ") || "online store", "Keywords")}
                  >
                    Copy
                  </button>
                </div>
                <input type="text" readOnly value={keywords.join(", ") || "online shopping, buy online"} />
              </div>
            </div>

            <footer className="store-modal-footer">
              <a
                href={storeKitModal.actionUrl}
                target="_blank"
                rel="noreferrer"
                className="store-btn-action submit-btn"
                style={{ padding: "8px 16px" }}
              >
                Go to {storeKitModal.name} ↗
              </a>
              <button
                type="button"
                className="store-field-helper-btn"
                onClick={() => setStoreKitModal(null)}
              >
                Close Kit
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* STORE MARK DONE MODAL */}
      {storeMarkDoneModal && (
        <div className="store-modal-overlay" onClick={() => setStoreMarkDoneModal(null)}>
          <div className="store-modal-card" onClick={(e) => e.stopPropagation()}>
            <header className="store-modal-header">
              <div>
                <h3 style={{ margin: "0 0 2px" }}>🔗 Verify Published Profile URL</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#94a3b8" }}>
                  {storeMarkDoneModal.citation?.name}
                </p>
              </div>
              <button
                type="button"
                className="store-modal-close"
                onClick={() => setStoreMarkDoneModal(null)}
              >
                ✕
              </button>
            </header>

            <form onSubmit={handleConfirmStoreMarkDone}>
              <div className="store-modal-body">
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "12px 14px", borderRadius: "8px" }}>
                  <p style={{ margin: 0, fontSize: "12.5px", color: "#166534" }}>
                    Paste the direct URL of your profile, page, or business listing where your store link is live.
                  </p>
                </div>

                <div className="store-kit-field">
                  <label style={{ fontWeight: 700, fontSize: "12.5px", color: "#0f172a" }}>
                    Live Profile / Post URL *
                  </label>
                  <input
                    type="url"
                    required
                    placeholder="e.g. https://instagram.com/yourstore or https://business.google.com/..."
                    value={storeMarkDoneModal.publishedUrl}
                    onChange={(e) =>
                      setStoreMarkDoneModal({ ...storeMarkDoneModal, publishedUrl: e.target.value })
                    }
                    autoFocus
                  />
                  <small style={{ color: "#64748b", fontSize: "11px" }}>
                    You can click this link anytime to check or update your listing.
                  </small>
                </div>

                <div className="store-kit-field">
                  <label style={{ fontWeight: 700, fontSize: "12.5px", color: "#0f172a" }}>
                    Notes (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Verified with phone OTP, website in bio"
                    value={storeMarkDoneModal.notes}
                    onChange={(e) =>
                      setStoreMarkDoneModal({ ...storeMarkDoneModal, notes: e.target.value })
                    }
                  />
                </div>
              </div>

              <footer className="store-modal-footer">
                <button
                  type="button"
                  className="store-field-helper-btn"
                  onClick={() => setStoreMarkDoneModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-store-save-seo"
                  style={{ padding: "8px 18px", fontSize: "13px" }}
                  disabled={savingCitation}
                >
                  {savingCitation ? <Spinner size={14} /> : "✓"}
                  <span>{savingCitation ? "Saving..." : "Verify & Mark Done"}</span>
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
